import { createApi, fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { RootState } from './index';
import { clearCustomerAuth, setCustomerAuth, setCustomerProfile, type CustomerProfile } from './customerAuthSlice';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8787';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).customerAuth.token;
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return headers;
  },
});

export const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status === 401) {
    api.dispatch(clearCustomerAuth());
  }
  return result;
};

export interface RegisterPayload {
  name: string;
  email: string;
  phone: string;
  address: string;
  addressLocality: string;
  addressProvince: string;
  addressPostalCode: string;
  password: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export type ProfileUpdatePayload = Partial<Pick<CustomerProfile,
  'name' | 'phone' | 'address' | 'addressLocality' | 'addressProvince' | 'addressPostalCode'
>>;

interface AuthResponse {
  token: string;
  customer: CustomerProfile;
}

export const customersApi = createApi({
  reducerPath: 'customersApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    register: build.mutation<AuthResponse, RegisterPayload>({
      query: (body) => ({ url: '/api/v1/customers/register', method: 'POST', body }),
      transformResponse: (r: { data: AuthResponse }) => r.data,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setCustomerAuth(data));
        } catch {
          // el componente que llama .unwrap() ya maneja el error
        }
      },
    }),
    login: build.mutation<AuthResponse, LoginPayload>({
      query: (body) => ({ url: '/api/v1/customers/login', method: 'POST', body }),
      transformResponse: (r: { data: AuthResponse }) => r.data,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setCustomerAuth(data));
        } catch {
          // el componente que llama .unwrap() ya maneja el error
        }
      },
    }),
    logout: build.mutation<void, void>({
      query: () => ({ url: '/api/v1/customers/logout', method: 'POST' }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } finally {
          dispatch(clearCustomerAuth());
        }
      },
    }),
    getProfile: build.query<CustomerProfile, void>({
      query: () => '/api/v1/customers/me',
      transformResponse: (r: { data: CustomerProfile }) => r.data,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setCustomerProfile(data));
        } catch {
          // sesión inválida ya la limpia baseQueryWithAuth vía el 401
        }
      },
    }),
    updateProfile: build.mutation<CustomerProfile, ProfileUpdatePayload>({
      query: (body) => ({ url: '/api/v1/customers/me', method: 'PATCH', body }),
      transformResponse: (r: { data: CustomerProfile }) => r.data,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setCustomerProfile(data));
        } catch {
          // el componente que llama .unwrap() ya maneja el error
        }
      },
    }),
    forgotPassword: build.mutation<{ message: string }, { email: string }>({
      query: (body) => ({ url: '/api/v1/customers/forgot-password', method: 'POST', body }),
      transformResponse: (r: { data: { message: string } }) => r.data,
    }),
    resetPassword: build.mutation<{ message: string }, { token: string; password: string }>({
      query: (body) => ({ url: '/api/v1/customers/reset-password', method: 'POST', body }),
      transformResponse: (r: { data: { message: string } }) => r.data,
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useLogoutMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} = customersApi;
